type AcceptUIOpaqueData = {
	dataDescriptor: string;
	dataValue: string;
};

type AcceptUIResponse = {
	messages: {
		resultCode: 'Ok' | 'Error';
		message: Array<{code: string; text: string}>;
	};
	opaqueData?: AcceptUIOpaqueData;
	encryptedCardData?: {
		cardNumber?: string;
		expDate?: string;
		bin?: string;
	};
};

type AcceptUIToken = {
	opaqueData: AcceptUIOpaqueData;
	createdAt: number;
	last4?: string;
	expDate?: string;
};

type AcceptUIButtonOptions = {
	apiLoginID: string;
	clientKey: string;
	responseHandler: string;
	buttonText?: string;
	headerText?: string;
};

const AUTHNET_ACCEPTUI_TOKEN_TTL_MS = 14 * 60 * 1000;
const AUTHNET_ACCEPTUI_SCRIPT_STATUS = 'data-peachpay-authnet-acceptui-status';

function createAcceptUIDataAttributes(options: AcceptUIButtonOptions): Record<string, string> {
	return {
		'data-apiLoginID': options.apiLoginID,
		'data-clientKey': options.clientKey,
		'data-paymentOptions': JSON.stringify({showCreditCard: true, showBankAccount: false}),
		'data-billingAddressOptions': JSON.stringify({show: false, required: false}),
		'data-responseHandler': options.responseHandler,
		'data-acceptUIFormBtnTxt': options.buttonText ?? 'Submit',
		'data-acceptUIFormHeaderTxt': options.headerText ?? 'Card Information',
	};
}

function getAcceptUIResponseError(response: AcceptUIResponse): string | null {
	if (response.messages.resultCode !== 'Error') {
		return null;
	}

	return response.messages.message?.[0]?.text ?? 'Card tokenization failed. Please try again.';
}

function createAcceptUIToken(response: AcceptUIResponse, now = Date.now()): AcceptUIToken | null {
	if (response.messages.resultCode !== 'Ok' || !response.opaqueData) {
		return null;
	}

	return {
		opaqueData: response.opaqueData,
		createdAt: now,
		last4: response.encryptedCardData?.cardNumber,
		expDate: response.encryptedCardData?.expDate,
	};
}

function isAcceptUITokenFresh(token: AcceptUIToken | null, now = Date.now()): token is AcceptUIToken {
	return Boolean(token && now - token.createdAt < AUTHNET_ACCEPTUI_TOKEN_TTL_MS);
}

function findAcceptUIScript(src: string): HTMLScriptElement | null {
	const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script.pp-authnet-acceptui-script'));
	return scripts.find(script => script.src === src || script.getAttribute('src') === src) ?? null;
}

async function loadAcceptUIScript(src: string): Promise<void> {
	return new Promise((resolve, reject) => {
		if (!src) {
			reject(new Error('Authorize.net hosted payment form URL is missing.'));
			return;
		}

		const existingScript = findAcceptUIScript(src);
		if (existingScript) {
			const status = existingScript.getAttribute(AUTHNET_ACCEPTUI_SCRIPT_STATUS);
			if (status === 'loaded') {
				resolve();
				return;
			}

			if (status === 'error') {
				existingScript.remove();
			} else {
				existingScript.addEventListener('load', () => {
					resolve();
				}, {once: true});
				existingScript.addEventListener('error', () => {
					reject(new Error('Failed to load Authorize.net hosted payment form.'));
				}, {once: true});
				return;
			}
		}

		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.src = src;
		script.async = true;
		script.className = 'pp-authnet-acceptui-script';
		script.setAttribute(AUTHNET_ACCEPTUI_SCRIPT_STATUS, 'loading');
		script.onload = () => {
			script.setAttribute(AUTHNET_ACCEPTUI_SCRIPT_STATUS, 'loaded');
			resolve();
		};

		script.onerror = () => {
			script.setAttribute(AUTHNET_ACCEPTUI_SCRIPT_STATUS, 'error');
			reject(new Error('Failed to load Authorize.net hosted payment form.'));
		};

		document.body.appendChild(script);
	});
}

export {
	AUTHNET_ACCEPTUI_TOKEN_TTL_MS,
	createAcceptUIDataAttributes,
	createAcceptUIToken,
	getAcceptUIResponseError,
	isAcceptUITokenFresh,
	loadAcceptUIScript,
	type AcceptUIOpaqueData,
	type AcceptUIResponse,
	type AcceptUIToken,
};
